import { ChevronLeft, CircleX, Megaphone } from 'lucide-react';

interface TopNavbarProps {
  agentName: string;
  level: number;
  /** When set (tickets list), Back chevron; when omitted (dashboard), title is DGSupport */
  onBack?: () => void;
  onClose?: () => void; // shown only when running inside SuperApp
  /**
   * Phase 37 — when provided (level>=2 only, gated in App.tsx), the name/level
   * chip becomes a tappable control that opens the canned-responses admin view.
   * When undefined (level<2), the chip stays a plain non-interactive label.
   */
  onOpenAdmin?: () => void;
  /**
   * Phase 39 — when provided (level>=2 only, gated in App.tsx via isAdmin),
   * renders a Megaphone icon button that opens the Announcements admin screen.
   * When undefined (level<2), the button is not rendered (entry hidden for L1).
   */
  onOpenAnnouncements?: () => void;
}

export function TopNavbar({ agentName, level, onBack, onClose, onOpenAdmin, onOpenAnnouncements }: TopNavbarProps) {
  return (
    <header className="flex-none bg-white border-b border-border-primary safe-area-top">
      {/* Inner row has fixed height — safe-area padding sits above it, not inside it */}
      <div className="h-[56px] flex items-center px-4 sm:px-6 gap-3 max-w-4xl mx-auto w-full">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-0.5 text-[#007AFF] px-1 -ml-1 min-w-[64px] touch-active"
            aria-label="Back"
          >
            <ChevronLeft className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="text-[17px] leading-none">Back</span>
          </button>
        ) : (
          <span className="text-lg font-bold text-[#0a0a0a] whitespace-nowrap">
            DGSupport
          </span>
        )}

        <div className="flex-1 flex items-center justify-end gap-3">
          {onOpenAnnouncements && (
            <button
              type="button"
              onClick={onOpenAnnouncements}
              className="w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:bg-gray-100 touch-active"
              aria-label="Announcements"
            >
              <Megaphone size={20} aria-hidden />
            </button>
          )}

          {onOpenAdmin ? (
            <button
              type="button"
              onClick={onOpenAdmin}
              className="text-sm font-medium text-text-secondary whitespace-nowrap touch-active rounded-md px-1 -mx-1 active:bg-gray-100"
              aria-label="Open canned-responses admin"
            >
              {agentName} | Lv {level} |
            </button>
          ) : (
            <span className="text-sm font-medium text-text-secondary whitespace-nowrap">
              {agentName} | Lv {level} |
            </span>
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-text-secondary"
              aria-label="Close"
            >
              <CircleX size={22} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
